/**
 * Domain Adapter — Plant / Process (CFIHOS 2.0)
 *
 * 119-plant-3d-conversion-strategy.md §12 Phase 5, Gap G-22.
 *
 * Hierarchy: ProcessUnit (LoR3) → System (LoR4) → Discipline (LoR5) → Tag (LoR6)
 *
 * When `context.fetchJson` is provided, attempts to fetch the plant hierarchy
 * sidecar at `{endpoint.href}.plant-hierarchy.json`. Falls back to a synthetic
 * manifest when the sidecar is absent or malformed.
 *
 * Per OBJECT_IDENTITY_AND_SELECTION_POLICY §7 / DESIGN_POLICY §5:
 * no domain-specific logic leaks into shared viewer code.
 */

import type { SceneItem } from "@/sdk-stub/gtwin-sdk/types";
import type {
  TreeNode,
  SceneHierarchyManifest,
  TreeNodeDomain,
} from "@/sdk-stub/gtwin-sdk/scene-hierarchy-types";
import type {
  DomainAdapter,
  DomainAdapterContext,
  AdapterResult,
} from "../DomainAdapter";

// ─────────────────────────────────────────────────────────────────────────────
// § 1  ULID generator
// ─────────────────────────────────────────────────────────────────────────────

const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

function generateUlid(): string {
  let t = Date.now();
  const tsChars: string[] = [];
  for (let i = 0; i < 10; i++) {
    tsChars.unshift(CROCKFORD[t % 32]);
    t = Math.floor(t / 32);
  }
  const randBytes = new Uint8Array(10);
  crypto.getRandomValues(randBytes);
  let rand = "";
  for (let i = 0; i < 10; i++) {
    rand += CROCKFORD[randBytes[i] & 0x1f];
    rand +=
      CROCKFORD[
        ((randBytes[i] >> 5) & 0x07) |
          ((i < 9 ? randBytes[i + 1] & 0x03 : 0) << 3)
      ];
  }
  return tsChars.join("") + rand.slice(0, 16);
}

// ─────────────────────────────────────────────────────────────────────────────
// § 2  Plant hierarchy sidecar types
//
// Produced by plant_dataset_converter.py → object-index.json and
// discipline-manifest.json. The sidecar aggregates all disciplines into a
// single JSON file at `{href}.plant-hierarchy.json`.
// ─────────────────────────────────────────────────────────────────────────────

interface PlantSidecarTag {
  tag: string;
  label: string;
  tagPattern?: string | null;
  classUri?: string | null;
  primitiveCount?: number;
  attributes?: Record<string, unknown>;
  featureId?: number | string | null;
}

interface PlantSidecarDiscipline {
  discipline: string;
  nodeCount: number;
  tagCount: number;
  tags: PlantSidecarTag[];
}

interface PlantSidecarSystem {
  system: string;
  disciplines: PlantSidecarDiscipline[];
}

interface PlantSidecar {
  schema: string;
  assetTag: string;
  assetKind?: string;
  intelligenceLevel: number;
  extractionConfidence: string;
  systems?: PlantSidecarSystem[];
  disciplines?: PlantSidecarDiscipline[];
  scopeMarkers?: string[];
}

function isValidPlantSidecar(raw: unknown): raw is PlantSidecar {
  if (typeof raw !== "object" || raw === null) return false;
  const r = raw as Record<string, unknown>;
  return (
    typeof r["schema"] === "string" &&
    r["schema"].startsWith("geosoft.plant-hierarchy") &&
    typeof r["assetTag"] === "string" &&
    typeof r["intelligenceLevel"] === "number"
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// § 3  Helpers
// ─────────────────────────────────────────────────────────────────────────────

const DOMAIN: TreeNodeDomain = "plant";
const PCA_RDL_BASE = "https://rds.posccaesar.org/ontology/plm/rdl/";

function countByLoR(nodes: TreeNode[]): Record<number, number> {
  const counts: Record<number, number> = {};
  for (const n of nodes) {
    counts[n.lor] = (counts[n.lor] ?? 0) + 1;
  }
  return counts;
}

function usnMeta(id: string, parentId: string | null) {
  return {
    objectId: id,
    aspects: {
      spatial: { parentId },
      functional: { parentId },
    },
    relationships: [] as const,
  };
}

function cfihosClassUri(classUri: string | null | undefined): string | null {
  if (!classUri) return null;
  if (classUri.startsWith("http")) return classUri;
  return `${PCA_RDL_BASE}${classUri}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// § 4  Synthetic manifest (fallback when no sidecar)
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_PLANT_DISCIPLINES = [
  "Piping",
  "Mechanical",
  "Electrical",
  "Instrumentation",
  "Civil / Structural",
];

function buildSyntheticManifest(
  item: SceneItem,
  asset: { layerId: string; assetNodeId: string; gtwinId: string },
  flags: { sidecarParseFailed?: boolean },
): AdapterResult {
  const rootId = generateUlid();
  const nodes: TreeNode[] = [];

  const rootMeta: Record<string, unknown> = { synthetic: true };
  if (flags.sidecarParseFailed !== undefined) {
    rootMeta["sidecarParseFailed"] = flags.sidecarParseFailed;
  }

  const root: TreeNode = {
    id: rootId,
    parentId: null,
    lor: 3,
    label: asset.assetNodeId || asset.layerId,
    kind: "asset",
    layerId: asset.layerId,
    featureId: null,
    domain: DOMAIN,
    standardRef: {
      standard: "CFIHOS:2.0",
      classUri: `${PCA_RDL_BASE}PCA_100003561`,
      nativeId: asset.assetNodeId,
      propertySets: [],
      linkedDocuments: [],
    },
    metadata: rootMeta,
    hasChildren: true,
    sortKey: 0,
    ...usnMeta(rootId, null),
  };
  nodes.push(root);

  for (let i = 0; i < DEFAULT_PLANT_DISCIPLINES.length; i++) {
    const discId = generateUlid();
    nodes.push({
      id: discId,
      parentId: rootId,
      lor: 5,
      label: DEFAULT_PLANT_DISCIPLINES[i],
      kind: "discipline",
      layerId: null,
      featureId: null,
      domain: DOMAIN,
      standardRef: null,
      metadata: { synthesised: true },
      hasChildren: false,
      sortKey: i,
      ...usnMeta(discId, rootId),
    });
  }

  return {
    ok: true,
    manifest: buildManifestEnvelope(item, asset, nodes),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// § 5  Sidecar-based manifest builder
//
// Maps: ProcessUnit (LoR3) → System (LoR4) → Discipline (LoR5) → Tag (LoR6)
//
// When the sidecar has no `systems` (Level 1 extraction), disciplines attach
// directly to the ProcessUnit root (LoR3 → LoR5 → LoR6).
// ─────────────────────────────────────────────────────────────────────────────

function buildFromSidecar(
  item: SceneItem,
  asset: { layerId: string; assetNodeId: string; gtwinId: string },
  sidecar: PlantSidecar,
): AdapterResult {
  const nodes: TreeNode[] = [];
  const rootId = generateUlid();

  const rootMeta: Record<string, unknown> = {
    intelligenceLevel: sidecar.intelligenceLevel,
    extractionConfidence: sidecar.extractionConfidence,
    assetKind: sidecar.assetKind ?? "ProcessUnit",
  };
  if (sidecar.scopeMarkers?.length) {
    rootMeta["scopeMarkers"] = sidecar.scopeMarkers;
  }

  const root: TreeNode = {
    id: rootId,
    parentId: null,
    lor: 3,
    label: sidecar.assetTag || asset.assetNodeId || asset.layerId,
    kind: "asset",
    layerId: asset.layerId,
    featureId: null,
    domain: DOMAIN,
    standardRef: {
      standard: "CFIHOS:2.0",
      classUri: `${PCA_RDL_BASE}PCA_100003561`,
      nativeId: sidecar.assetTag,
      propertySets: [],
      linkedDocuments: [],
    },
    metadata: rootMeta,
    hasChildren: true,
    sortKey: 0,
    ...usnMeta(rootId, null),
  };
  nodes.push(root);

  if (sidecar.systems?.length) {
    let sysIdx = 0;
    for (const sys of sidecar.systems) {
      const sysId = generateUlid();
      const sysNode: TreeNode = {
        id: sysId,
        parentId: rootId,
        lor: 4,
        label: sys.system,
        kind: "group",
        layerId: asset.layerId,
        featureId: null,
        domain: DOMAIN,
        standardRef: null,
        metadata: { systemLabel: sys.system },
        hasChildren: sys.disciplines.length > 0,
        sortKey: sysIdx,
        ...usnMeta(sysId, rootId),
      };
      nodes.push(sysNode);

      emitDisciplines(nodes, sys.disciplines, sysId, asset, sidecar);
      sysIdx++;
    }
  } else if (sidecar.disciplines?.length) {
    emitDisciplines(nodes, sidecar.disciplines, rootId, asset, sidecar);
  }

  return {
    ok: true,
    manifest: buildManifestEnvelope(item, asset, nodes),
  };
}

function emitDisciplines(
  nodes: TreeNode[],
  disciplines: PlantSidecarDiscipline[],
  parentId: string,
  asset: { layerId: string; assetNodeId: string; gtwinId: string },
  sidecar: PlantSidecar,
): void {
  let discIdx = 0;
  for (const disc of disciplines) {
    const discId = generateUlid();
    const discNode: TreeNode = {
      id: discId,
      parentId,
      lor: 5,
      label: disc.discipline,
      kind: "discipline",
      layerId: null,
      featureId: null,
      domain: DOMAIN,
      standardRef: null,
      metadata: {
        tagCount: disc.tagCount,
        nodeCount: disc.nodeCount,
      },
      hasChildren: disc.tags.length > 0,
      childCount: disc.tags.length,
      sortKey: discIdx,
      ...usnMeta(discId, parentId),
    };
    nodes.push(discNode);

    let tagIdx = 0;
    for (const tag of disc.tags) {
      const tagId = generateUlid();
      const resolvedClassUri = cfihosClassUri(tag.classUri);

      const tagMeta: Record<string, unknown> = {
        tagPattern: tag.tagPattern,
        primitiveCount: tag.primitiveCount,
        intelligenceLevel: sidecar.intelligenceLevel,
        extractionConfidence: sidecar.extractionConfidence,
      };
      if (tag.attributes) {
        Object.assign(tagMeta, tag.attributes);
      }

      const tagNode: TreeNode = {
        id: tagId,
        parentId: discId,
        lor: 6,
        label: tag.label || tag.tag,
        kind: "element",
        layerId: asset.layerId,
        featureId: tag.featureId != null ? `feature:${tag.featureId}` : null,
        domain: DOMAIN,
        standardRef: resolvedClassUri
          ? {
              standard: "CFIHOS:2.0",
              classUri: resolvedClassUri,
              nativeId: tag.tag,
              propertySets: [],
              linkedDocuments: [],
            }
          : null,
        metadata: tagMeta,
        hasChildren: false,
        sortKey: tagIdx,
        ...usnMeta(tagId, discId),
      };
      nodes.push(tagNode);
      tagIdx++;
    }
    discIdx++;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// § 6  Manifest envelope
// ─────────────────────────────────────────────────────────────────────────────

function buildManifestEnvelope(
  item: SceneItem,
  asset: { layerId: string; assetNodeId: string; gtwinId: string },
  nodes: TreeNode[],
): SceneHierarchyManifest {
  return {
    schema: "geosoft.scene-hierarchy/1",
    gtwinId: asset.gtwinId,
    rootLayerId: asset.layerId,
    generatedAt: new Date().toISOString(),
    domain: DOMAIN,
    standardName: "CFIHOS",
    standardVersion: "2.0",
    totalNodes: nodes.length,
    nodeCountByLoR: countByLoR(nodes),
    nodes,
    paginated: false,
    dataSources: {
      [asset.layerId]: {
        layerId: asset.layerId,
        label: "Plant Model",
        mediaKind: item.layerKind,
        primary: true,
      },
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// § 7  Adapter implementation
// ─────────────────────────────────────────────────────────────────────────────

export const CfihosPlantAdapter: DomainAdapter = {
  domain: DOMAIN,
  version: "1.0.0",
  standard: "CFIHOS:2.0",

  detect(item: SceneItem): boolean {
    type Extended = SceneItem & {
      extensions?: { geosoft?: { domain?: string } };
    };
    const ext = (item as Extended).extensions?.geosoft;
    if (ext?.domain === "plant") return true;
    return item.layerKind === "cfihos";
  },

  async buildManifest(
    item: SceneItem,
    asset: { layerId: string; assetNodeId: string; gtwinId: string },
    context: DomainAdapterContext,
  ): Promise<AdapterResult> {
    const href =
      item.endpoints["href"] ?? Object.values(item.endpoints)[0] ?? "";

    if (!context.fetchJson) {
      return buildSyntheticManifest(item, asset, {});
    }

    const sidecarUrl = `${href}.plant-hierarchy.json`;

    let raw: unknown;
    try {
      raw = await context.fetchJson(sidecarUrl);
    } catch {
      return buildSyntheticManifest(item, asset, {
        sidecarParseFailed: false,
      });
    }

    if (!isValidPlantSidecar(raw)) {
      return buildSyntheticManifest(item, asset, {
        sidecarParseFailed: true,
      });
    }

    try {
      return buildFromSidecar(item, asset, raw);
    } catch {
      return buildSyntheticManifest(item, asset, {
        sidecarParseFailed: true,
      });
    }
  },
};
